import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {LoginRequestPayload, LoginSuccessPayload, LoginFailurePayload, RbacPayload} from '@store/actions/authActions';
import {USER_INFO} from "@constants/lsconstants";

/** Quyền của người đang đăng nhập. failed: không tải được quyền -> không chặn gì trên FE. */
export type RbacState = RbacPayload & { failed?: boolean };

interface AuthState {
    token: string | null;
    user_id: number | null;
    email: string | null;
    role: string | null;
    loading: boolean;
    error: string | null;
    access_token: string;
    avatar_url: string | null;
    full_name: string | null;
    username: string | null;
    k12_username: string | null;
    session_key: string | null;
    // Pending login data when a session conflict is detected
    sessionConflict: boolean;
    pendingLogin: (LoginSuccessPayload & { session_key?: string }) | null;
    // null = chưa tải quyền (login SSO, vừa đăng nhập): saga gọi /api/rbac/me/
    rbac: RbacState | null;
}

const loadStateFromLocalStorage = (): AuthState => {
    const serializedState = localStorage.getItem(USER_INFO);
    if (serializedState === null) {
        return {
            token: null,
            user_id: null,
            email: null,
            role: null,
            loading: false,
            error: null,
            access_token: '',
            avatar_url: '',
            full_name: '',
            username: null,
            k12_username: null,
            session_key: null,
            sessionConflict: false,
            pendingLogin: null,
            rbac: null,
        };
    }
    const parsed = JSON.parse(serializedState) as Partial<AuthState>;
    return {
        ...parsed,
        username: parsed.username ?? null,
        k12_username: parsed.k12_username ?? null,
        session_key: parsed.session_key ?? null,
        sessionConflict: false,
        pendingLogin: null,
        rbac: parsed.rbac ?? null,
    } as AuthState;
};

const initialState: AuthState = loadStateFromLocalStorage();

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginRequest(state, action: PayloadAction<LoginRequestPayload>) {
            state.loading = true;
            state.error = null;
            state.sessionConflict = false;
            state.pendingLogin = null;
        },
        loginSuccess(state, action: PayloadAction<LoginSuccessPayload & { session_key?: string }>) {
            state.user_id = action.payload.user_id;
            state.email = action.payload.email;
            state.role = action.payload.role;
            state.loading = false;
            state.error = null;
            state.access_token = action.payload.access_token;
            state.avatar_url = action.payload.avatar_url;
            state.full_name = action.payload.full_name;
            state.username = action.payload.username;
            state.k12_username = action.payload.k12_username ?? null;
            state.session_key = action.payload.session_key ?? null;
            state.sessionConflict = false;
            state.pendingLogin = null;
            // Không giữ quyền của người đăng nhập trước; thiếu rbac thì saga tải lại
            state.rbac = action.payload.rbac ?? null;
            localStorage.setItem(USER_INFO, JSON.stringify({
                ...state,
                sessionConflict: false,
                pendingLogin: null,
            }));
        },
        loginFailure(state, action: PayloadAction<LoginFailurePayload>) {
            state.loading = false;
            state.error = action.payload.error;
        },
        sessionConflictDetected(state, action: PayloadAction<LoginSuccessPayload & { session_key?: string }>) {
            state.loading = false;
            state.sessionConflict = true;
            state.pendingLogin = action.payload;
        },
        clearSessionConflict(state) {
            state.sessionConflict = false;
            state.pendingLogin = null;
            state.loading = false;
        },
        setRbac(state, action: PayloadAction<RbacState>) {
            state.rbac = action.payload;
            // Lưu cùng USER_INFO (chỉ khi đang đăng nhập) để F5 vẫn có quyền trước khi tải lại xong
            const serializedState = localStorage.getItem(USER_INFO);
            if (serializedState !== null) {
                localStorage.setItem(USER_INFO, JSON.stringify({...JSON.parse(serializedState), rbac: action.payload}));
            }
        },
        logout(state) {
            state.rbac = null;
            state.access_token = null;
            state.user_id = null;
            state.email = null;
            state.role = null;
            state.loading = false;
            state.error = null;
            state.avatar_url = null;
            state.username = null;
            state.k12_username = null;
            state.session_key = null;
            state.sessionConflict = false;
            state.pendingLogin = null;
            localStorage.removeItem(USER_INFO);
        },
    },
});

export const {
    loginRequest,
    loginSuccess,
    loginFailure,
    sessionConflictDetected,
    clearSessionConflict,
    setRbac,
    logout
} = authSlice.actions;

export default authSlice.reducer;
